import React, { useState, useEffect, useCallback } from 'react';
import { GameState, TileData, VikingData, CombatResult } from '../types/GameTypes';
import TileComponent from './TileComponent';
import ActiveEventCard from './ActiveEventCard';
import GameControls from './GameControls';
import CombatResultModal from './CombatResultModal';
import socketService from '../services/socketService';
import { MessageType } from '../services/WebSocketMessageHandler';
import './GameBoard.css';
import { useAudioManager } from '../hooks/useAudioManager';

interface GameBoardProps {
  gameId: string;
  onTileClick?: (tile: TileData) => void;
  showVikings?: boolean;
  localPlayerId?: string;
  onPlayCard: (cardId: string) => void;
}

const GameBoard: React.FC<GameBoardProps> = ({ gameId, onTileClick, showVikings = true, localPlayerId, onPlayCard }) => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [validMoves, setValidMoves] = useState<({x: number, y: number})[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedViking, setSelectedViking] = useState<VikingData | null>(null);
  const [attackableVikings, setAttackableVikings] = useState<VikingData[]>([]);
  const [combatResult, setCombatResult] = useState<CombatResult | null>(null);
  const [showCombatModal, setShowCombatModal] = useState(false);
  const audioManager = useAudioManager();

  const fetchVikings = useCallback(async (id: string) => {
    // This should be replaced by websocket communication
  }, []);

  useEffect(() => {
    audioManager.loadSound('tile-flip', '/sounds/tile-flip.wav');
    audioManager.loadSound('combat-start', '/sounds/combat-start.wav');

    const handleCombatResolved = (message: any) => {
      setCombatResult(message.payload);
      setShowCombatModal(true);
    };

    const handleGameStateUpdate = (message: any) => {
      if (message.payload.id === gameId) {
        setGameState(message.payload);
        setIsLoading(false);
      }
    };

    // Register message handlers
    socketService.addMessageHandler(MessageType.COMBAT_RESOLVED, handleCombatResolved);
    socketService.addMessageHandler(MessageType.GAME_STATE_UPDATE, handleGameStateUpdate);

    // Request initial game state
    socketService.getGameState(gameId);

    return () => {
      socketService.removeMessageHandler(MessageType.COMBAT_RESOLVED, handleCombatResolved);
      socketService.removeMessageHandler(MessageType.GAME_STATE_UPDATE, handleGameStateUpdate);
    };
  }, [audioManager, gameId, fetchVikings]);

  useEffect(() => {
    if (gameState && localPlayerId) {
      const isMyTurn = gameState.currentPlayer === localPlayerId;
      if (isMyTurn) {
        fetchValidMoves(gameState.id, localPlayerId);
      } else {
        setValidMoves([]);
      }
    }
  }, [gameState, localPlayerId]);

  const fetchValidMoves = async (gId: string, pId: string) => {
    try {
              const response = await fetch(`https://vikings.tenb.uk/api/viking/${gId}/${pId}/valid-moves`);
      if (response.ok) {
        const data = await response.json();
        setValidMoves(data.validMoves);
      }
    } catch (err) {
      console.error("Failed to fetch valid moves:", err);
    }
  };

  const getVikingsAtPosition = (x: number, y: number): VikingData[] => {
    if (!gameState) return [];
    return gameState.players.filter(viking => viking.position.x === x && viking.position.y === y);
  };

  const isTileHighlighted = (x: number, y: number) => {
    return validMoves.some(move => move.x === x && move.y === y);
  };

  const getAttackableVikings = (attacker: VikingData): VikingData[] => {
    if (!attacker || !gameState) return [];
    const adjacentPositions = [
      { x: attacker.position.x - 1, y: attacker.position.y },
      { x: attacker.position.x + 1, y: attacker.position.y },
      { x: attacker.position.x, y: attacker.position.y - 1 },
      { x: attacker.position.x, y: attacker.position.y + 1 },
    ];
    return gameState.players.filter(viking =>
      viking.playerId !== attacker.playerId &&
      viking.isAlive &&
      adjacentPositions.some(p => p.x === viking.position.x && p.y === viking.position.y)
    );
  };

  const handleVikingClick = (viking: VikingData) => {
    if (viking.playerId === localPlayerId) {
      setSelectedViking(viking);
      setAttackableVikings(getAttackableVikings(viking));
    }
  };

  const initiateCombat = (attackerId: string, defenderId: string) => {
    audioManager.playSound('combat-start');
    socketService.startCombat(attackerId, defenderId, gameId);
    setSelectedViking(null);
    setAttackableVikings([]);
  };

  const handleTileClick = (tile: TileData) => {
    if (!tile.revealed && gameId) {
      socketService.revealTile(gameId, tile.x, tile.y);
      audioManager.playSound('tile-flip');
    }
    if (onTileClick) onTileClick(tile);
  };

  const handleCloseCombatModal = () => {
    setShowCombatModal(false);
    setCombatResult(null);
  };

  if (isLoading) return <div className="loading">Loading game board...</div>;
  if (error) return <div className="error"><p>❌ {error}</p></div>;
  if (!gameState) return <div className="no-data">No game data available</div>;

  const boardData = gameState.board;

  return (
    <div className="game-board-container">
      <ActiveEventCard card={gameState.currentEventCard} />
      <div className="board-header">
        <h2>Viking Ears Game Board</h2>
        <p>Round: {gameState.round}</p>
        {gameState.currentPlayer && <p>Current Player: {gameState.currentPlayer}</p>}
      </div>
      <div className="game-board" style={{ gridTemplateColumns: `repeat(${boardData.width}, 1fr)`, gridTemplateRows: `repeat(${boardData.height}, 1fr)` }}>
        {boardData.grid.map((row, y) =>
          row.map((tile, x) => (
            <TileComponent
              key={`${x}-${y}`}
              tile={tile}
              position={{ x, y }}
              onClick={tile ? () => handleTileClick(tile) : undefined}
              vikings={showVikings ? getVikingsAtPosition(x, y) : []}
              onVikingClick={handleVikingClick}
              attackableVikings={attackableVikings}
              selectedViking={selectedViking}
              initiateCombat={initiateCombat}
              isHighlighted={isTileHighlighted(x, y)}
            />
          ))
        )}
      </div>
      {showCombatModal && combatResult && <CombatResultModal result={combatResult} onClose={handleCloseCombatModal} />}
      <div className="board-legend"><h3>Legend</h3>{/* Legend items */}</div>
      {showVikings && gameId && <GameControls gameId={gameId} onPlayCard={onPlayCard} />}
    </div>
  );
};

export default GameBoard;
